import { useColorMode, useEventListener } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { UserRole } from '@/enums/user'
import dayjs, { setZone } from '@/libs/dayjs'
import i18n from '@/libs/i18n'
import type { ITenant } from '@/types/Tenant'
import type { IUser } from '@/types/User'

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const locale       = ref(localStorage.getItem('locale') || 'sl')
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<IUser>()
    const tenant       = ref<ITenant | null>(null)
    /** Set when the signed-in user is linked to an employee — only then is "Moj urnik" shown. */
    const employeeId   = ref<string | null>(null)

    // Whether GET /me has answered for this token. The router waits on it, so no screen
    // renders before it knows which menu the account gets.
    const sessionLoaded = ref(false)

    // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() —
    // never a second source of truth.
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    useEventListener(window, 'resize', () => { windowWidth.value = window.innerWidth })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    const role            = computed(() => userData.value?.role ?? '')
    const isPlatformAdmin = computed(() => role.value === UserRole.PlatformAdmin)
    const isAdmin         = computed(() => role.value === UserRole.Admin)

    // Sets a JWT cookie so that API requests for files loaded via <img src> or <a href>
    // are authenticated — browsers cannot set Authorization headers for those.
    const setJWTCookie = (jwt: string) => {
        if (jwt) {
            document.cookie = `JWT=${jwt}; path=/; SameSite=Lax`
        } else {
            document.cookie = 'JWT=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
        }
    }

    setJWTCookie(JWT.value)

    const setLogin = (jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
        setJWTCookie(jwt)
        sessionLoaded.value = false
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value = ''
        setJWTCookie('')
        userData.value = undefined
        tenant.value = null
        employeeId.value = null
        sessionLoaded.value = false
    }

    const setSession = (user: IUser, t: ITenant | null, linkedEmployeeId: string | null = null) => {
        userData.value = user
        tenant.value = t
        employeeId.value = linkedEmployeeId
        setZone(t?.timezone)
        sessionLoaded.value = true
    }

    const setTenant = (t: ITenant) => {
        tenant.value = t
        setZone(t.timezone)
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        ;(i18n.global.locale as unknown as { value: string }).value = newLocale
        dayjs.locale(newLocale)
    }

    return {
        JWT, locale, userData, tenant, employeeId, loaderActive, colorMode, sessionLoaded,
        role, isPlatformAdmin, isAdmin,
        getJWT, isLoggedIn, isLoaderActive, isMobile,
        setLogin, setLogout, setSession, setTenant, startLoading, stopLoading, changeLocale,
    }
})
